import React, { useState, useEffect } from 'react';
import { Building2, TrendingUp, AlertTriangle, XCircle, Edit, Trash2 } from 'lucide-react';
import { useBrand } from '../../contexts/BrandContext';
import { forecastService, ForecastClient } from '../../lib/forecastService';
import { useToast } from '../../contexts/ToastContext';
import ClientDetail from './ClientDetail';

interface ClientListProps {
  onRefresh: () => void;
}

const ClientList: React.FC<ClientListProps> = ({ onRefresh }) => {
  const { currentBrand } = useBrand();
  const { showToast } = useToast();
  const [clients, setClients] = useState<ForecastClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState<ForecastClient | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'abandoned'>('all');

  useEffect(() => {
    if (currentBrand) {
      loadClients();
    }
  }, [currentBrand]);

  const loadClients = async () => {
    if (!currentBrand) return;

    try {
      setLoading(true);
      const data = await forecastService.getClients(currentBrand.id);
      setClients(data);
    } catch (error) {
      showToast('Failed to load clients', 'error');
      console.error('Error loading clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (clientId: string) => {
    if (!confirm('Are you sure you want to delete this client? This will also delete all associated services and projects.')) {
      return;
    }

    try {
      await forecastService.deleteClient(clientId);
      showToast('Client deleted successfully', 'success');
      loadClients();
      onRefresh();
    } catch (error) {
      showToast('Failed to delete client', 'error');
      console.error('Error deleting client:', error);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <TrendingUp className="w-5 h-5 text-green-600" />;
      case 'inactive':
        return <XCircle className="w-5 h-5 text-gray-600" />;
      case 'abandoned':
        return <AlertTriangle className="w-5 h-5 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'abandoned':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredClients = statusFilter === 'all'
    ? clients
    : clients.filter(c => c.status === statusFilter);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-white rounded-lg border border-gray-200">
        <p className="text-gray-500">Loading clients...</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              statusFilter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setStatusFilter('active')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              statusFilter === 'active'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Active
          </button>
          <button
            onClick={() => setStatusFilter('inactive')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              statusFilter === 'inactive'
                ? 'bg-gray-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Inactive
          </button>
          <button
            onClick={() => setStatusFilter('abandoned')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              statusFilter === 'abandoned'
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Abandoned
          </button>
        </div>

        {filteredClients.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">No clients found</p>
            <p className="text-gray-500 text-sm mt-1">
              {statusFilter === 'all'
                ? 'Add your first client to start forecasting'
                : `No ${statusFilter} clients`}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredClients.map((client) => (
              <div
                key={client.id}
                className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedClient(client)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{client.company_name}</h3>
                      {client.contact_name && (
                        <p className="text-sm text-gray-600 mt-1">{client.contact_name}</p>
                      )}
                      {client.contact_email && (
                        <p className="text-sm text-gray-500 truncate">{client.contact_email}</p>
                      )}
                      <div className="flex items-center gap-2 mt-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(client.status)}`}>
                          {getStatusIcon(client.status)}
                          {client.status.charAt(0).toUpperCase() + client.status.slice(1)}
                        </span>
                        <span className="text-xs text-gray-500">
                          Score: {client.payment_reliability_score}/100
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => setSelectedClient(client)}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="View Details"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(client.id)}
                      className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete Client"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {client.notes && (
                  <p className="text-sm text-gray-600 mt-3 line-clamp-2">{client.notes}</p>
                )}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">
                      Started: {new Date(client.activation_date).toLocaleDateString()}
                    </span>
                    {client.deactivation_date && (
                      <span className="text-gray-600">
                        Ended: {new Date(client.deactivation_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedClient && (
        <ClientDetail
          client={selectedClient}
          onClose={() => setSelectedClient(null)}
          onUpdate={() => {
            loadClients();
            onRefresh();
          }}
        />
      )}
    </>
  );
};

export default ClientList;